import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ArrowForwardIos, Search } from '@mui/icons-material';
import SquareRoundedIcon from '@mui/icons-material/SquareRounded';
import {
  Box,
  Checkbox,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';

import CustomButton from 'components/Button';
import { FilterButtons, FilterPanelContainer, ItemListContainer, PageContainer } from 'components/GlobalStyles/styles';
import GridItem from 'components/GridItem';
import Loader from 'components/Loader';
import SnackBar, { MessageType } from 'components/SnackBar';
import NewJob from 'features/NewJob';
import { useGetActiveJobsQuery, useGetAllJobsQuery, useGetManagerListQuery } from 'services/busmanApi';
import { ClientProps, JobProps } from 'types/WorkLogTypes';

import { RootState } from '../../store';

import { Job } from './styles';

const STATUSES = ['All', 'InProgress', 'Completed'] as const;
const ACTIVE_STATUSES = ['Active', 'Inactive', 'All'] as const;
const ITEMS_PER_PAGE = 20;

const STATUS_CONFIG = {
  Approved: 'busmanGreen',
  Pending: 'busmanPrimary',
  Synced: 'lightGrey',
  'In Progress': 'busmanPrimary',
  InProgress: 'busmanPrimary',
  Completed: 'busmanGreen',
  Billed: 'lightGrey',
} as const;

const useStatusConfig = () => {
  const theme = useTheme();

  return React.useMemo(
    () => ({
      getChipColor: (status: string) =>
        theme.palette.busmanColors[STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]] ||
        theme.palette.busmanColors.busmanPrimary,
      getStatusColor: (status: string) => {
        switch (status) {
          case 'Billed':
            return theme.palette.busmanColors.lightGrey;
          case 'In Progress':
            return theme.palette.busmanColors.busmanPrimary;
          case 'Completed':
            return theme.palette.busmanColors.busmanGreen;
          default:
            return theme.palette.busmanColors.busmanPrimary;
        }
      },
    }),
    [theme.palette.busmanColors],
  );
};

const StatusChip = React.memo(
  ({ jobStatus, count, isMobileScreen }: { jobStatus: string; count?: number; isMobileScreen: boolean }) => {
    const theme = useTheme();
    const { getChipColor } = useStatusConfig();

    const displayStatus = jobStatus === 'InProgress' ? 'In Progress' : jobStatus;
    const chipColor = getChipColor(jobStatus);

    return (
      <Chip
        size="small"
        label={count !== undefined ? count : displayStatus.toUpperCase()}
        variant="outlined"
        sx={{
          color: chipColor,
          border: !isMobileScreen ? `1px solid ${chipColor}` : 'none',
          fontSize: '10px',
          '.MuiChip-avatar': { width: 'auto' },
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.5rem',
          },
        }}
      />
    );
  },
);

StatusChip.displayName = 'StatusChip';

const JobRow = React.memo(
  ({
    job,
    clients,
    onJobClick,
    style = {},
    theme,
    isMobileScreen,
  }: {
    job: JobProps;
    clients: ClientProps[];
    onJobClick: (job: JobProps) => void;
    style: React.CSSProperties;
    theme: Theme;
    isMobileScreen: boolean;
  }) => {
    const { getStatusColor } = useStatusConfig();
    const jobClient = React.useMemo(
      () => clients.find((client) => client.client_id === job.client?.client_id),
      [clients, job.client?.client_id],
    );

    const handleClick = React.useCallback(() => {
      onJobClick(job);
    }, [job, onJobClick]);

    return (
      <Job
        container
        key={job.job_number}
        onClick={handleClick}
        style={{
          ...style,
          minHeight: '72px',
          height: '72px',
          display: 'flex',
          alignItems: 'center',
        }}
        sx={{ mb: 0.1, border: 1, borderColor: theme.palette.background.default }}>
        <GridItem title={job.job_number} seenOrIsHeading={false} breakpoint={isMobileScreen ? 1.8 : 2} />
        {!isMobileScreen && <GridItem title={jobClient?.client_name} seenOrIsHeading={false} breakpoint={2} />}
        <GridItem title={job.job_description} seenOrIsHeading={false} breakpoint={isMobileScreen ? 4.5 : 4} />
        <GridItem
          title={<StatusChip jobStatus={job.job_status} isMobileScreen={isMobileScreen} />}
          seenOrIsHeading={false}
          breakpoint={isMobileScreen ? 3 : 2}
        />
        <GridItem
          title={
            <Checkbox
              checked={job.is_active}
              checkedIcon={
                <SquareRoundedIcon
                  sx={{
                    backgroundColor: theme.palette.background.default,
                    padding: '1px',
                    color: getStatusColor(job.job_status),
                  }}
                />
              }
              sx={{
                padding: 0,
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '& .MuiSvgIcon-root': {
                  width: '15px',
                  height: '15px',
                  color: getStatusColor(job.job_status),
                  backgroundColor: theme.palette.background.default,
                  borderRadius: '3px',
                },
              }}
            />
          }
          breakpoint={isMobileScreen ? 1.5 : 1}
          seenOrIsHeading={false}
        />
        <GridItem
          title={<ArrowForwardIos sx={{ color: theme.palette.text.disabled, fontSize: '0.8rem' }} />}
          seenOrIsHeading={false}
          breakpoint={1}
        />
      </Job>
    );
  },
);

JobRow.displayName = 'JobRow';

const useJobFiltering = (jobList: JobProps[] | undefined, inputText: string, status: string, currentPage: number) => {
  const filteredJobs = React.useMemo(() => {
    if (!jobList) return [];

    return jobList
      .filter((data) => {
        if (inputText === '') return true;
        return data?.job_number.toLowerCase().includes(inputText.toLowerCase());
      })
      .filter((data) => {
        if (status === STATUSES[0]) return true;
        return data?.job_status === status;
      })
      .sort((a, b) => a.job_number.localeCompare(b.job_number));
  }, [jobList, inputText, status]);

  const totalItems = filteredJobs.length;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  const paginatedJobs = React.useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredJobs.slice(startIndex, endIndex);
  }, [filteredJobs, currentPage]);

  return {
    filteredJobs,
    paginatedJobs,
    totalItems,
    totalPages,
  };
};

const JobsPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [addNewJob, setAddNewJob] = React.useState(false);
  const [inputText, setInputText] = React.useState('');
  const [message, setMessage] = React.useState<MessageType | null>(null);
  const [status, setStatus] = React.useState<string>(STATUSES[0]);
  const [activeInactive, setActiveInactive] = React.useState<string>('Active');
  const [currentPage, setCurrentPage] = React.useState(1);

  const { clients, jobs } = useSelector((state: RootState) => state.worklogs);
  const { filterPanel } = useSelector((state: RootState) => state.filterPanel);

  const jobsResult = useGetActiveJobsQuery({});
  const { data: jobManagers } = useGetManagerListQuery({});
  const { data: allJobs, isFetching: isFetchingAllJobs } = useGetAllJobsQuery(
    {},
    { skip: activeInactive === 'Active' },
  );

  const jobList = React.useMemo(() => {
    if (activeInactive === 'Active') return jobs;
    if (!allJobs) return [];

    const inActiveJobList = allJobs.filter((job) => !job.is_active);
    return activeInactive === 'Inactive' ? inActiveJobList : allJobs;
  }, [activeInactive, jobs, allJobs]);

  const { filteredJobs, paginatedJobs, totalPages } = useJobFiltering(jobList, inputText, status, currentPage);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [inputText, status, activeInactive]);

  const handleCloseSnackbar = React.useCallback(() => {
    setMessage(null);
  }, []);

  const handleOpenSnackbar = React.useCallback((displayMessage: MessageType) => {
    setMessage(displayMessage);
  }, []);

  const handleJobClick = React.useCallback(
    (selectedJob: JobProps) => {
      navigate(`/jobs/${selectedJob?.job_id}`);
    },
    [navigate],
  );

  const handlePageChange = React.useCallback((_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  }, []);

  const handleInputChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value);
  }, []);

  const handleStatusChange = React.useCallback((event: SelectChangeEvent<string>) => {
    setStatus(event.target.value);
  }, []);

  const handleActiveInactiveChange = React.useCallback((event: SelectChangeEvent<string>) => {
    setActiveInactive(event.target.value);
  }, []);

  const handleNewJobClick = React.useCallback(() => {
    setAddNewJob(true);
  }, []);

  const handleCloseNewJob = React.useCallback(() => {
    setAddNewJob(false);
  }, []);

  const renderJobs = () => (
    <ItemListContainer
      sx={{
        [theme.breakpoints.down('lg')]: {
          display: addNewJob ? 'none' : '',
        },
      }}>
      <Grid container display="flex" flexDirection="row">
        <GridItem title={'JOB NUMBER'} seenOrIsHeading={true} breakpoint={isMobileScreen ? 1.8 : 2} />
        {!isMobileScreen && <GridItem title={'CLIENT'} seenOrIsHeading={true} breakpoint={2} />}
        <GridItem title={'DESCRIPTION'} seenOrIsHeading={true} breakpoint={isMobileScreen ? 4.7 : 4.2} />
        <GridItem title={'STATUS'} seenOrIsHeading={true} breakpoint={isMobileScreen ? 2.5 : 1.7} />
        <GridItem title={'ACTIVE'} seenOrIsHeading={true} breakpoint={2} />
      </Grid>

      {filteredJobs.length === 0 && (
        <Typography sx={{ padding: theme.spacing(2) }} variant="h6">
          No results found...
        </Typography>
      )}

      {paginatedJobs.map((job) => (
        <JobRow
          key={job.job_number}
          job={job}
          clients={clients}
          onJobClick={handleJobClick}
          style={{}}
          theme={theme}
          isMobileScreen={isMobileScreen}
        />
      ))}

      {filteredJobs.length > 1 && totalPages > 1 && (
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: theme.spacing(3),
          }}
        />
      )}
    </ItemListContainer>
  );

  const isFetching = jobsResult.isFetching;

  return (
    <>
      <Helmet>
        <title>BusMin</title>
        <meta name="Busmin" content="EEA-Jobs" />
      </Helmet>
      <Loader open={isFetching || isFetchingAllJobs} />
      <PageContainer>
        {filterPanel && !addNewJob && (
          <FilterPanelContainer>
            {!isMobileScreen && (
              <Typography gutterBottom sx={{ mb: theme.spacing(2) }} variant="h4">
                Jobs
              </Typography>
            )}
            <OutlinedInput
              endAdornment={
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              }
              onChange={handleInputChange}
              placeholder="Search Job Number"
              sx={{ height: '32px', mb: theme.spacing(2) }}
              value={inputText}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography gutterBottom variant="body2">
                STATUS
              </Typography>
              <Select
                onChange={handleStatusChange}
                sx={{
                  height: '32px',
                  mb: theme.spacing(2),
                  [theme.breakpoints.down('md')]: {
                    width: '100%',
                  },
                }}
                value={status}>
                {STATUSES.map((jobStatus) => (
                  <MenuItem key={jobStatus} value={jobStatus}>
                    {jobStatus}
                  </MenuItem>
                ))}
              </Select>
              <Typography gutterBottom variant="body2">
                ACTIVE/INACTIVE
              </Typography>
              <Select
                onChange={handleActiveInactiveChange}
                sx={{
                  height: '32px',
                  [theme.breakpoints.down('md')]: {
                    width: '100%',
                  },
                }}
                value={activeInactive}>
                {ACTIVE_STATUSES.map((jobStatus) => (
                  <MenuItem key={jobStatus} value={jobStatus}>
                    {jobStatus}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <FilterButtons sx={{ marginLeft: 'auto' }}>
              <CustomButton variant="contained" onClick={handleNewJobClick} width="medium" text="New Job" />
            </FilterButtons>
          </FilterPanelContainer>
        )}
        {renderJobs()}
        {addNewJob && (
          <NewJob close={handleCloseNewJob} showMessage={handleOpenSnackbar} jobManagers={jobManagers || []} />
        )}
      </PageContainer>
      <SnackBar message={message} onClose={handleCloseSnackbar} />
    </>
  );
};

export default JobsPage;
